// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { Stack, Center, Container } from '@/components/ui/layout';
import { Card } from '@/components/ui/data-display';
import { Title, Text, Button, Badge } from '@/components/ui/core';
import { PasswordInput } from '@/components/ui/inputs';
import { IconLock, IconArrowLeft, IconLockCheck, IconCube, IconCheck } from '@tabler/icons-react';
import { FadeIn, SlideIn, HoverScale } from '@/components/animations';
import { ParallaxBackground, PasswordStrength } from '../components';

export default function ResetParallax() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isReset, setIsReset] = useState(false);
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsReset(true);
  };

  const passwordsMatch = password === confirmPassword;
  const canSubmit = password.length >= 8 && passwordsMatch;

  return (
    <ParallaxBackground preset="stars" mouseParallax intensity={20}>
      <Center mih="100vh" p="md">
        <Container size="xs" w="100%">
          <SlideIn direction="up" delay={100} duration={600}>
            <Card
              p="xl"
              radius="lg"
              style={{
                backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(16px)',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
              }}
            >
              {isReset ? (
                <Stack gap="lg" align="center">
                  <FadeIn delay={100}>
                    <Center
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: 20,
                        backgroundColor: 'var(--mantine-color-green-light)',
                      }}
                    >
                      <IconCheck size={40} color="var(--mantine-color-green-6)" />
                    </Center>
                  </FadeIn>

                  <FadeIn delay={200}>
                    <Stack align="center" gap="xs">
                      <Title order={2} ta="center">Password Reset!</Title>
                      <Text c="dimmed" ta="center" maw={300}>
                        Your password has been successfully reset.
                      </Text>
                    </Stack>
                  </FadeIn>

                  <FadeIn delay={300}>
                    <HoverScale scale={1.02}>
                      <Button
                        component={Link}
                        to="/app/demo/auth-pages/login/parallax"
                        fullWidth
                        size="md"
                        radius="md"
                        variant="gradient"
                        gradient={{ from: 'indigo', to: 'violet' }}
                      >
                        Continue to login
                      </Button>
                    </HoverScale>
                  </FadeIn>
                </Stack>
              ) : (
                <Stack gap="lg">
                  <Button
                    component={Link}
                    to="/app/demo/auth-pages/reset-password"
                    variant="subtle"
                    size="xs"
                    leftSection={<IconArrowLeft size={14} />}
                    w="fit-content"
                  >
                    Back to variants
                  </Button>

                  <FadeIn delay={200}>
                    <Stack align="center" gap="xs">
                      <Badge
                        leftSection={<IconCube size={12} />}
                        variant="gradient"
                        gradient={{ from: 'indigo', to: 'violet' }}
                        size="lg"
                      >
                        Parallax Effect
                      </Badge>
                      <Center
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 14,
                          backgroundColor: 'var(--mantine-color-indigo-light)',
                          marginTop: 'var(--mantine-spacing-sm)',
                        }}
                      >
                        <IconLockCheck size={28} color="var(--mantine-color-indigo-6)" />
                      </Center>
                      <Title order={2} ta="center" mt="sm">
                        Set new password
                      </Title>
                      <Text c="dimmed" size="sm" ta="center" maw={300}>
                        Your new password must be different from previous passwords.
                      </Text>
                    </Stack>
                  </FadeIn>

                  <form onSubmit={handleSubmit}>
                    <Stack gap="md">
                      <FadeIn delay={300}>
                        <div>
                          <PasswordInput
                            label="New password"
                            placeholder="Enter new password"
                            leftSection={<IconLock size={16} />}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            size="md"
                            radius="md"
                          />
                          {password && <PasswordStrength password={password} variant="bar" />}
                        </div>
                      </FadeIn>

                      <FadeIn delay={400}>
                        <PasswordInput
                          label="Confirm password"
                          placeholder="Confirm new password"
                          leftSection={<IconLock size={16} />}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          size="md"
                          radius="md"
                          error={confirmPassword && !passwordsMatch ? "Passwords don't match" : undefined}
                        />
                      </FadeIn>

                      <FadeIn delay={500}>
                        <HoverScale scale={1.02}>
                          <Button
                            type="submit"
                            fullWidth
                            size="md"
                            radius="md"
                            variant="gradient"
                            gradient={{ from: 'indigo', to: 'violet' }}
                            disabled={!canSubmit}
                          >
                            Reset password
                          </Button>
                        </HoverScale>
                      </FadeIn>
                    </Stack>
                  </form>

                  <FadeIn delay={600}>
                    <Center>
                      <Button
                        component={Link}
                        to="/app/demo/auth-pages/login/parallax"
                        variant="subtle"
                        leftSection={<IconArrowLeft size={14} />}
                      >
                        Back to login
                      </Button>
                    </Center>
                  </FadeIn>
                </Stack>
              )}
            </Card>
          </SlideIn>
        </Container>
      </Center>
    </ParallaxBackground>
  );
}
